import { LIMIT, LIMIT_MAX } from '@/constants';
import { useAppStore } from '@/store';
import { CustomerSourceType, PlaceOrderType, SettingKey, ShareSettingPageKey, Sort, UserCouponState } from '@/types';
import {
  fetchActiveCustomerDocument,
  fetchAddCustomerSourceDocument,
  fetchAddCustomerSourceMiniProgramDocument,
  fetchAddDistributorShareProductDocumenet,
  fetchCancelAllMarkUpDocument,
  fetchCancelGiftDocument,
  fetchCancelMarkUpDocument,
  fetchClickAdvertisingSaveDocument,
  fetchFloatingWindowDocument,
  fetchGetCouponBundleDocument,
  fetchGetFloatingWindowCouponRecordDocument,
  fetchGetPointsProductsDocument,
  fetchGetPurchaseQuantityDocument,
  fetchHotWordsDocument,
  fetchOrderListWithPickupDocument,
  fetchOrderListWithoutPickupDocument,
  fetchPendingOrdersNumberDocument,
  fetchPickupStoresDocument,
  fetchProdcutVariantsDocument,
  fetchProductActivitiesFindAllDocument,
  fetchProductDetailDocument,
  fetchProductsSortByProductIdsDocument,
  fetchReceiveCouponBundleDocumenet,
  fetchRegionsJsonDocument,
  fetchRemoveAllOrderLinesByOrderTypeDocument,
  fetchReportedDistributorRecordDocument,
  fetchSelectGiftDocument,
  fetchSelectMarkUpDocument,
  fetchSettingsDocument,
  fetchShareSettingsDocument,
  fetchUserClickBuriedPointDocument,
  fetchUserClickProductBuriedPointDocument,
  fetchUserCouponDocument,
  fetchClaimCouponDocumenet,
  fetchGetCouponHoldDocument,
  fetchUserCouponsDocumenet,
  fetchUserDeviceDocument,
  getSubscribeMessageTemplateIdDocument,
  fetchGetWeappDynamicRouteConfigDocument,
  fetchMembershipCardActivationDocument,
  fetchCreateCouponExpiredNoticeSubscriptionRecordDocumenet,
  fetchPickupOrderByCodeDocument,
  fetchConfirmPickupDocument,
  fetchCustomNoticeSubscribeScenesDocument,
  fetchUpsertCustomNoticeSubscriptionDocument,
} from '../graphql/docs';
import { fetcher } from '../graphql/fetcher';

/**
 * 获取浮窗数据
 */
export const fetchFloatingWindow = async () => {
  const res = await fetcher.query(fetchFloatingWindowDocument, {});
  return res.data.value?.floatingWindow;
};

/**
 * 获取浮窗优惠券记录
 */
export const fetchGetFloatingWindowCouponRecord = async () => {
  const res = await fetcher.query(fetchGetFloatingWindowCouponRecordDocument, {});
  return res.data.value?.getFloatingWindowCouponRecord;
};

/**
 * 获取全局分享设置
 */
export const fetchShareSettings = async (pageKeys: ShareSettingPageKey[]) => {
  const res = await fetcher.query(fetchShareSettingsDocument, {
    variables: {
      pageKeys,
    },
  });
  return res.data.value?.shareSettings;
};

/**
 * 获取精选配置商品
 * @param keyNames
 */
export const fetchSettings = async (keyNames: SettingKey[]) => {
  const res = await fetcher.query(fetchSettingsDocument, {
    variables: {
      keyNames,
    },
  });
  return res.data.value?.settings;
};

/**
 * 删除全部订单 - 直接购买
 */
export const fetchRemoveAllOrderByRegularOrder = async () => {
  const res = await fetcher.mutation(fetchRemoveAllOrderLinesByOrderTypeDocument, {
    type: PlaceOrderType.regularOrder,
  });
  return res.data?.removeAllOrderLinesByOrderType;
};

/**
 * 获取优惠券礼包详信息
 * @param couponBundleId - 活动id
 */
export const fetchGetCouponBundle = async (couponBundleId: string) => {
  const res = await fetcher.query(fetchGetCouponBundleDocument, {
    variables: {
      id: couponBundleId,
    },
  });
  return res.data.value?.couponBundle;
};

/**
 * 领取优惠券礼包
 * @param receiveCouponBundleId - 优惠券礼包id
 */
export const fetchReceiveCouponBundle = async (receiveCouponBundleId: string) => {
  const res = await fetcher.mutation(fetchReceiveCouponBundleDocumenet, {
    id: receiveCouponBundleId,
  });
  return res.data?.receiveCouponBundle;
};

/**
 * 获取用户优惠卷详情
 * @param couponId - 用户优惠券id
 * @returns
 */
export const fetchUserCouponDetail = async (userCouponId: string, removeNewTag = false) => {
  const res = await fetcher.query(fetchUserCouponDocument, {
    variables: {
      userCouponId,
      removeNewTag,
    },
  });
  return res.data.value?.userCoupon;
};

/**
 * 获取用户全部优惠卷
 */
export const fetchCouponList = async (isUnused: boolean, ids?: Array<string>) => {
  let state: AnyObject = {};
  let id: AnyObject = {};
  const condition = [UserCouponState.unused, UserCouponState.notStarted];
  if (isUnused) {
    state.in = condition;
  } else {
    state.notIn = condition;
  }
  if (ids && ids.length) {
    id.in = ids
  }
  const res = await fetcher.query(fetchUserCouponsDocumenet, {
    variables: {
      options: {
        take: LIMIT_MAX,
        filter: {
          state,
          id
        },
        sort: {
          maturityAt: 'DESC',
        },
      },
    },
  });
  return res.data.value?.userCoupons?.items;
};

/**
 * 领取优惠券
 * @param couponId - 优惠券id
 */
export const fetchClaimCoupon = async (couponId: string) => {
  const res = await fetcher.mutation(fetchClaimCouponDocumenet, {
    couponId,
  });
  return res.data?.claimCoupon;
};

/**
 * 领取优惠券后订阅
 * @param userCouponId - 优惠券id
 */
export const fetchCreateCouponExpiredNoticeSubscriptionRecord = async (userCouponId: string) => {
  const res = await fetcher.mutation(fetchCreateCouponExpiredNoticeSubscriptionRecordDocumenet, {
    userCouponId,
  });
  return res.data?.createCouponExpiredNoticeSubscriptionRecord;
};

/**
 * 获取用户的优惠券持有信息
 * @param couponId - 优惠券id
 */
export const fetchGetCouponHold = async (couponId: string) => {
  const res = await fetcher.query(fetchGetCouponHoldDocument, {
    variables: {
      couponId,
    },
  });
  return res.data.value?.getCouponHold;
};

/**
 * 搜索热词
 */
export const fetchSearchHotWords = async () => {
  const res = await fetcher.query(fetchHotWordsDocument, {
    variables: {
      options: {
        take: LIMIT_MAX,
        sort: {
          createdAt: Sort.DESC,
        },
      },
    },
  });
  return res.data.value?.hotWords?.items;
};

/**
 * 分销员点赚添加推广商品
 * @param productId
 * @returns
 */
export const fetchAddDistributorShareProduct = async (productId: string) => {
  const res = await fetcher.mutation(fetchAddDistributorShareProductDocumenet, {
    productId,
  });
  return res.data?.addDistributorShareProduct;
};

/**
 * 获取地址省市区数据资源
 * @param level
 * @param parentCode
 * @returns
 */
export const fetchRegionsJsons = async (regionNames: string[]) => {
  const res = await fetcher.query(fetchRegionsJsonDocument, {
    variables: { regionNames },
  });
  return res.data.value?.getRegionsJson;
};

/**
 * 获取地址列表
 */
export const fetchAddressList = async () => {
  const res = await fetcher.query(fetchActiveCustomerDocument, {
    variables: { take: LIMIT_MAX },
  });
  return res.data.value?.activeCustomer?.addresses;
};

/**
 * 获取积分商品列表
 * @param variables
 * @returns
 */
export const fetchGetPointsProducts = async (variables: {
  isAllowExchange: boolean;
  options: AnyObject;
}) => {
  const res = await fetcher.query(fetchGetPointsProductsDocument, {
    variables,
  });
  return res.data.value?.getPointsProducts;
};

/**
 * 商品对应的所有活动信息数据
 * @param productId - 商品id
 */
export const fetchProductActivitiesFindAll = async (productId: string) => {
  const res = await fetcher.query(fetchProductActivitiesFindAllDocument, {
    variables: {
      productId,
    },
  });
  return res.data.value?.productActivitiesFindAll;
};

/**
 *
 * @param input
 * @returns
 */
export const fetchClickAdvertisingSave = async (input: AnyObject) => {
  const res = await fetcher.mutation(fetchClickAdvertisingSaveDocument, {
    input,
  });
  return res.data?.clickAdvertisingSave;
};

/**
 * 程序启动（热启动）上报
 */
export const fetchUserClickBuriedPoint = async () => {
  await fetcher.mutation(fetchUserClickBuriedPointDocument, {});
};

/**
 * 商品点击上报
 * @param productId - 商品id
 */
export const fetchUserClickProductBuriedPoint = async (productId: string) => {
  await fetcher.mutation(fetchUserClickProductBuriedPointDocument, {
    productId,
  });
};

/**
 * 用户设备信息上报
 * @param productId - 商品id
 */
export const fetchUserDevice = async (openIdKey: string, deviceInfo: AnyObject) => {
  if (!openIdKey) return;
  await fetcher.mutation(fetchUserDeviceDocument, {
    openIdKey,
    deviceInfo,
  });
};

/**
 * 分销信息上报
 * @param md5Str
 * @param sourceCode
 * @param distributionId
 */
export const fetchReportedDistributorRecord = async (
  md5Str: string,
  sourceCode: string,
  distributionId = ''
) => {
  const res = await fetcher.mutation(fetchReportedDistributorRecordDocument, {
    md5Str,
    sourceCode,
    distributionId,
  });
  console.log(`分销信息上报 => `, {
    requestParams: {
      md5Str,
      sourceCode,
      distributionId,
    },
    responseData: res.data,
  });
};

/**
 * 来源信息上报
 * @param sourceType
 * @param sourceValue
 */
export const fetchAddCustomerSource = async (
  sourceType: CustomerSourceType,
  sourceValue: string
) => {
  await fetcher.mutation(fetchAddCustomerSourceDocument, {
    customerSourceInput: {
      sourceType,
      sourceValue,
    },
  });
};

/**
 * 小程序来源信息上报
 * @param customerReferralInput
 */
export const fetchAddCustomerSourceMiniProgram = async (customerReferralInput: {
  sourceType: string;
  sourceValue: string;
  additionalInfo?: string;
}) => {
  await fetcher.mutation(fetchAddCustomerSourceMiniProgramDocument, {
    customerReferralInput,
  });
};

/**
 * 添加赠品
 * @param orderId
 * @param skuId
 * @param giftId
 * @param promInstanceId
 */
export const fetchSelectGift = async (
  orderId: string,
  skuId: string,
  giftId: string,
  promInstanceId: string,
  ladderLevel: number
) => {
  const res = await fetcher.mutation(fetchSelectGiftDocument, {
    orderId,
    skuId,
    giftId,
    promInstanceId,
    ladderLevel,
  });
  return res.data?.selectGift;
};

/**
 * 取消赠品
 * @param orderId
 */
export const fetchCancelGift = async (orderId: string, giftId: string, promInstanceId: string) => {
  const res = await fetcher.mutation(fetchCancelGiftDocument, {
    orderId,
    giftId,
    promInstanceId,
  });
  return res.data?.cancelGift;
};

/**
 * 选择优惠换购商品
 * @param orderId
 * @param skuId
 * @param productId
 * @param promInstanceId
 */
export const fetchSelectMarkUp = async (
  orderId: string,
  skuId: string,
  productId: string,
  promInstanceId: string
) => {
  const res = await fetcher.mutation(fetchSelectMarkUpDocument, {
    orderId,
    skuId,
    productId,
    promInstanceId,
  });
  return res.data?.selectMarkUp;
};

/**
 * 取消选择优惠换购商品
 * @param orderId
 * @param productId
 * @param promInstanceId
 */
export const fetchCancelMarkUp = async (
  orderId: string,
  productId: string,
  promInstanceId: string
) => {
  const res = await fetcher.mutation(fetchCancelMarkUpDocument, {
    orderId,
    productId,
    promInstanceId,
  });
  return res.data?.cancelMarkUp;
};

/**
 * 取消加价购全部商品
 * @param orderId - 订单id
 */
export const fetchCancelAllMarkUp = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelAllMarkUpDocument, {
    orderId,
  });
  return res.data?.cancelAllMarkUp;
};

/**
 * 查询商品限购数量
 * @param productId - 商品id
 */
export const fetchGetPurchaseQuantity = async (
  productId: string,
  type: PlaceOrderType = PlaceOrderType.regularOrder,
  isIncludeCurrentOrder: boolean = true
) => {
  const res = await fetcher.query(fetchGetPurchaseQuantityDocument, {
    variables: {
      productId,
      type,
      isIncludeCurrentOrder,
    },
  });
  return res.data.value?.getPurchaseQuantity;
};

/**
 * 获取商品规格
 * @param id - 商品id
 */
export const fetchProdcutVariants = async (id: string) => {
  const res = await fetcher.query(fetchProdcutVariantsDocument, {
    variables: { id },
  });
  return res.data.value?.product;
};

/**
 * 获取商品详情
 * @param id - 商品id
 */
export const fetchProductDetails = async (id: string) => {
  const res = await fetcher.query(fetchProductDetailDocument, {
    variables: { id },
  });
  return res.data.value?.product;
};

/**
 * 查询订单栏各待办数量
 */
export const fetchPendingOrdersNumber = async () => {
  const res = await fetcher.query(fetchPendingOrdersNumberDocument, {});
  return res.data.value?.pendingOrdersNumber;
};

/**
 * 获取 是否包含产品id 的商品数据 - 自定义排序
 * @param include - 是否包含
 * @param productIds - 产品ids
 * @returns
 */
export const fetchProductsSortByProductIds = async (
  include?: boolean,
  productIds?: string[],
  take = 50
) => {
  const idConf =
    typeof include === 'undefined' ? {} : include ? { in: productIds } : { notIn: productIds };
  const res = await fetcher.query(fetchProductsSortByProductIdsDocument, {
    variables: {
      options:
        typeof include === 'undefined'
          ? { take }
          : {
              take,
              filter: {
                id: idConf,
              },
            },
    },
  });
  return res.data.value?.productsSortByProductIds?.items;
};

/**
 * 获取订单列表
 * @param skip - 数据略过个数
 */
export const fetchOrderList = async (skip: number, filterState: AnyObject, take?: number) => {
  const appStore = useAppStore();
  const enablePickup = appStore.channelConfig?.enablePickup ?? false;
  const document = enablePickup ? fetchOrderListWithPickupDocument : fetchOrderListWithoutPickupDocument;
  const res = await fetcher.query(document, {
    variables: {
      options: {
        take: take ?? LIMIT,
        skip: LIMIT * skip,
        filter: {
          state: filterState,
          isHidden: {
            eq: false,
          },
        },
        sort: {
          timeToPlaceOrder: Sort.DESC,
        },
      },
    },
  });
  return res.data.value?.orders?.items;
};

/**
 * 获取渠道配置信息
 */
export const fetchGetSubscribeMessageTemplateId = async () => {
  const res = await fetcher.query(getSubscribeMessageTemplateIdDocument, {});
  return res.data.value?.getSubscribeMessageTemplateId;
};

/**
 * 获取tabbar和功能页面配置信息
 */
export const fetchGetWeappDynamicRouteConfig = async () => {
  const res = await fetcher.query(fetchGetWeappDynamicRouteConfigDocument, {});
  return res.data.value?.getWeappDynamicRouteConfig;
};

/**
 * 激活会员
 * @param variables
 */
export const fetchMembershipCardActivation = async (variables: AnyObject) => {
  const res = await fetcher.mutation(fetchMembershipCardActivationDocument, variables);
  return res.data?.membershipCardActivation;
};

/**
 * 获取自提门店列表
 * @param latitude - 纬度（可选，用于计算距离）
 * @param longitude - 经度（可选，用于计算距离）
 */
export const fetchPickupStores = async (latitude?: number, longitude?: number) => {
  const res = await fetcher.query(fetchPickupStoresDocument, {
    variables: {
      latitude,
      longitude,
    },
  });
  return res.data.value?.pickupStores;
};

/**
 * 扫描提货码查询订单信息
 * @param pickupCode - 提货码
 */
export const fetchPickupOrderByCode = async (pickupCode: string) => {
  const res = await fetcher.query(fetchPickupOrderByCodeDocument, {
    variables: {
      pickupCode,
    },
  });
  return res.data.value?.pickupOrderByCode;
};

/**
 * 确认提货
 * @param pickupCode - 提货码
 */
export const fetchConfirmPickup = async (pickupCode: string) => {
  const res = await fetcher.mutation(fetchConfirmPickupDocument, {
    pickupCode
  });
  return res.data?.confirmPickup;
};

/**
 * 获取全部场景的自定义订阅通知配置
 */
export const fetchCustomNoticeSubscribeScenes = async () => {
  const res = await fetcher.query(fetchCustomNoticeSubscribeScenesDocument, {});
  return res.data.value?.customNoticeSubscribeScenes;
};

/**
 * 授权自定义订阅通知后记录到数据库
 * @param noticeTemplateIds - 授权成功的服务端模版主键集合
 */
export const fetchUpsertCustomNoticeSubscription = async (noticeTemplateIds: string[]) => {
  const res = await fetcher.mutation(fetchUpsertCustomNoticeSubscriptionDocument, {
    noticeTemplateIds,
  });
  return res.data?.upsertCustomNoticeSubscription;
};
